/**
 * Worker profile modal — the "edit everything" form Luke was missing.
 */
import { useState } from "react";
import { errMsg } from "@/lib/hsUtil";
import api from "@/lib/api";
import NumberInput from "@/components/NumberInput";
import DateInput from "@/components/DateInput";

const contactsText = (v) => typeof v === "string" ? v
  : (v || []).map(c => [c.name, c.relationship, c.phone].filter(Boolean).join(" | ")).join("\n");
const textToContacts = (t) => t.split("\n").map(l => l.trim()).filter(Boolean).map(l => {
  const [name = "", relationship = "", phone = ""] = l.split("|").map(s => s.trim());
  return { name, relationship, phone };
});

// Module-level so inputs keep focus (an inner component remounts on every keystroke).
function F({ label, children }) {
  return (
    <label className="block text-xs">
      <span className="block text-silver mb-0.5">{label}</span>
      {children}
    </label>
  );
}

export default function WorkerProfileModal({ worker, onClose, onSaved }) {
  const [w, setW] = useState({ ...worker });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  async function save() {
    setBusy(true); setErr("");
    try {
      const body = {
        name: w.name, email: w.email, role: w.role,
        pay_type: w.pay_type || "Hourly",
        pay_rate: parseFloat(w.pay_rate || 0),
        salary: parseFloat(w.salary || 0),
        hours_per_week: parseFloat(w.hours_per_week || 45.42),
        salary_period: w.salary_period || "weekly",
        contract_rate: parseFloat(w.contract_rate || 0),
        contract_rate_type: w.contract_rate_type || "hourly",
        gst_registered: !!w.gst_registered, gst_number: w.gst_number || "",
        ir330c_source: w.ir330c_source || "",
        ir330c_rate: parseFloat(w.ir330c_rate || 0),
        ir330c_date: w.ir330c_date || "",
        tax_code: w.tax_code || "",
        ird_number: w.ird_number || "",
        bank_account: w.bank_account || "",
        bank_account_name: w.bank_account_name || "",
        emergency_contacts: typeof w.emergency_contacts === "string" ? textToContacts(w.emergency_contacts) : (w.emergency_contacts || []),
        xero_link: w.xero_link || "",
        start_date: w.start_date || "",
        charge_out_margin_pct: parseFloat(w.charge_out_margin_pct ?? 15),
      };
      // Only send cost_rate / source when user actually set manual
      if (w.cost_rate_source === "manual") {
        body.cost_rate = parseFloat(w.cost_rate || 0);
        body.cost_rate_source = "manual";
      } else if (w.cost_rate_source === "template") {
        body.cost_rate_source = "template";
      }
      await api.patch(`/workers/${w.id}`, body);
      if (w.propagate_cost_rate) {
        const r = await api.post(`/workers/${w.id}/propagate-cost-rate`, { confirm: false });
        if (window.confirm(`Change cost rate on ${r.data.timesheets_touched} existing timesheets?\nBefore: $${r.data.before_total}\nAfter: $${r.data.after_total}\nDiff: $${r.data.diff}`)) {
          await api.post(`/workers/${w.id}/propagate-cost-rate`, { confirm: true });
        }
      }
      onSaved?.();
    } catch (e) { setErr(errMsg(e, "Save failed. Please try again.")); }
    finally { setBusy(false); }
  }
  return (
    <div className="fixed inset-0 bg-black/80 z-40 flex items-center justify-center p-4" data-testid="worker-profile-modal">
      <div className="card max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-silver">Edit worker — {worker.name}</h3>
          <button className="opacity-70" onClick={onClose}>×</button>
        </div>
        {err && <div className="text-red-400 text-sm mb-2" data-testid="wp-save-error">{err}</div>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <F label="Name"><input className="input" value={w.name||""} onChange={e=>setW({...w,name:e.target.value})} data-testid="wp-name"/></F>
          <F label="Email"><input className="input" value={w.email||""} onChange={e=>setW({...w,email:e.target.value})} data-testid="wp-email"/></F>
          <F label="Role"><select className="input" value={w.role||"Worker"} onChange={e=>setW({...w,role:e.target.value})} data-testid="wp-role">
            <option>Admin</option><option>Manager</option><option>Worker</option>
          </select></F>
          <F label="Pay type"><select className="input" value={w.pay_type||"Hourly"} onChange={e=>setW({...w,pay_type:e.target.value})} data-testid="wp-pay-type">
            <option>Hourly</option><option>Salaried</option><option>Contractor</option>
          </select></F>

          {(w.pay_type||"Hourly") === "Hourly" && (
            <>
              <F label="Pay rate ($/h)"><NumberInput className="input" money value={w.pay_rate||0} onChange={e=>setW({...w,pay_rate:e.target.value})} data-testid="wp-pay-rate"/></F>
              <F label={<span>Cost rate ($/h) <span className="opacity-60">({w.cost_rate_source||"template"})</span></span>}>
                <div className="flex gap-1 items-center">
                  <NumberInput className="input flex-1" money value={w.cost_rate||0}
                       onChange={e=>setW({...w,cost_rate:e.target.value, cost_rate_source: "manual"})}
                       data-testid="wp-cost-rate"/>
                  {(w.cost_rate_source === "manual") && (
                    <button type="button" className="hub-link text-[11px]"
                            onClick={()=>setW({...w,cost_rate_source:"template"})} data-testid="wp-recalc-from-template">Recalculate from template</button>
                  )}
                </div>
              </F>
            </>
          )}

          {w.pay_type === "Salaried" && (
            <>
              <F label="Annual salary (gross $)"><NumberInput className="input" money value={w.salary||0} onChange={e=>setW({...w,salary:e.target.value})} data-testid="wp-salary"/></F>
              <F label="Hours per week" hint="default 5 × 9h05m = 45.42">
                <NumberInput className="input" value={w.hours_per_week||45.42} onChange={e=>setW({...w,hours_per_week:e.target.value})} data-testid="wp-hpw"/></F>
              <F label="Hourly equivalent (auto)">
                <input className="input opacity-70" disabled value={`$${((parseFloat(w.salary||0)/(52*parseFloat(w.hours_per_week||45.42)))||0).toFixed(2)}`} data-testid="wp-hourly-eq"/></F>
              <F label="Pay period"><select className="input" value={w.salary_period||"weekly"} onChange={e=>setW({...w,salary_period:e.target.value})} data-testid="wp-sal-period">
                <option value="weekly">Weekly</option><option value="fortnightly">Fortnightly</option>
              </select></F>
            </>
          )}

          {w.pay_type === "Contractor" && (
            <>
              <F label="Contract rate type"><select className="input" value={w.contract_rate_type||"hourly"} onChange={e=>setW({...w,contract_rate_type:e.target.value})} data-testid="wp-ctr-type">
                <option value="hourly">$ per hour</option>
                <option value="day">$ per day</option>
                <option value="job">Agreed amount per project</option>
              </select></F>
              <F label="Contract rate ($)"><NumberInput className="input" money value={w.contract_rate||0} onChange={e=>setW({...w,contract_rate:e.target.value})} data-testid="wp-ctr-rate"/></F>
              <F label="GST registered"><label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={!!w.gst_registered} onChange={e=>setW({...w,gst_registered:e.target.checked})} data-testid="wp-gst-reg"/> Yes — add 15% GST on top (not a cost)</label></F>
              {w.gst_registered && <F label="GST number"><input className="input" value={w.gst_number||""} onChange={e=>setW({...w,gst_number:e.target.value})} data-testid="wp-gst-num"/></F>}
              <F label="IR330C on file"><select className="input" value={w.ir330c_source||""} onChange={e=>setW({...w,ir330c_source:e.target.value})} data-testid="wp-ir330c-src">
                <option value="">— No IR330C on file (⚠ 45% withheld)</option>
                <option value="standard">Standard schedular rate</option>
                <option value="tailored">Tailored rate</option>
                <option value="chosen">Own chosen rate (min 10%)</option>
              </select></F>
              {w.ir330c_source && <F label="Withholding rate %"><NumberInput className="input" value={w.ir330c_rate||0} onChange={e=>setW({...w,ir330c_rate:e.target.value})} data-testid="wp-ir330c-rate"/></F>}
              {w.ir330c_source && <F label="IR330C date"><DateInput className="input" value={w.ir330c_date||""} onChange={e=>setW({...w,ir330c_date:e.target.value})} data-testid="wp-ir330c-date"/></F>}
              {!w.ir330c_source && (
                <F label=""><div className="text-xs text-amber-300 border border-amber-400/50 rounded p-2" data-testid="wp-no-ir330c-warn">
                  ⚠ No IR330C on file — 45% will be withheld (IRD no-notification rate).
                </div></F>
              )}
              <F label=""><div className="text-xs opacity-70 border border-neutral-800 rounded p-2" data-testid="wp-ctr-note">
                Employer on-costs <b>do not</b> apply to contractors: ACC levy, KiwiSaver, annual leave, public holidays, sick leave and tools are greyed out — Contractor pays their own. Whether someone is really a contractor or an employee depends on the real nature of the relationship (Employment Relations Act s6), not on the label. Confirm with your accountant or employment adviser.
              </div></F>
            </>
          )}

          <F label={<span>Charge-out margin % <button type="button" className="hub-link text-[11px] ml-1" onClick={()=>setW({...w,charge_out_margin_pct:15})} data-testid="wp-use-default-margin">Use default 15%</button></span>}>
            <NumberInput className="input" value={w.charge_out_margin_pct ?? 15} onChange={e=>setW({...w,charge_out_margin_pct:e.target.value})} data-testid="wp-margin"/>
          </F>
          <F label="Charge-out rate ($/h, auto)"><input className="input opacity-70" disabled value={`$${((w.cost_rate||0)*(1+((w.charge_out_margin_pct??15)/100))).toFixed(2)}`} data-testid="wp-charge-out"/></F>
          <F label="Tax code"><input className="input" value={w.tax_code||""} onChange={e=>setW({...w,tax_code:e.target.value})} data-testid="wp-tax-code"/></F>
          <F label="IRD number"><input className="input" value={w.ird_number||""} onChange={e=>setW({...w,ird_number:e.target.value})} data-testid="wp-ird"/></F>
          <F label="Bank account"><input className="input" value={w.bank_account||""} onChange={e=>setW({...w,bank_account:e.target.value})} data-testid="wp-bank"/></F>
          <F label="Bank account name"><input className="input" value={w.bank_account_name||""} onChange={e=>setW({...w,bank_account_name:e.target.value})} data-testid="wp-bank-name"/></F>
          <F label="Start date"><DateInput className="input" value={w.start_date||""} onChange={e=>setW({...w,start_date:e.target.value})} data-testid="wp-start"/></F>
          <F label="Xero link (optional)"><input className="input" value={w.xero_link||""} onChange={e=>setW({...w,xero_link:e.target.value})} data-testid="wp-xero"/></F>
          <F label="Emergency contacts">
            <textarea className="input" rows="2" placeholder="One per line: Name | Relationship | Phone" value={contactsText(w.emergency_contacts)} onChange={e=>setW({...w,emergency_contacts:e.target.value})} data-testid="wp-emergency"/>
          </F>
        </div>
        <div className="mt-4 flex gap-2 flex-wrap">
          <button className="btn-silver" onClick={save} disabled={busy} data-testid="btn-wp-save">{busy?"Saving…":"Save"}</button>
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
